import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import Scrollbar from "../components/scrollbar";
import { get, del, post } from "src/utils/networkUtils";
import PromotionsTable from "src/components/promotions/PromotionsTable";
import { useNavigate } from "react-router-dom";
// ----------------------------------------------------------------------

export default function PromotionsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activePromotions, setActivePromotions] = useState(null);
  const [inactivePromotions, setInactivePromotions] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promotionToDelete, setPromotionToDelete] = useState(null);

  useEffect(() => {
    get(`/admin/promotions?status=active`).then(function (response) {
      setLoading(false);
      setActivePromotions(response.promotions);
    });
    get(`/admin/promotions?status=inactive`).then(function (response) {
      setLoading(false);
      setInactivePromotions(response.promotions);
    });

  }, []);

  const handleCreatePromotion = () => {
    navigate('/dashboard/promotions/promotion');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getCurrentPromotions = () => {
    return activeTab === 0 ? activePromotions : inactivePromotions;
  };

  const handleDeleteClick = (promotion) => {
    setPromotionToDelete(promotion);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPromotionToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (promotionToDelete) {
      setLoading(true);
      del(`/admin/promotions/${promotionToDelete.promotionId}`)
        .then(function (response) {
          setLoading(false);
          setDeleteDialogOpen(false);
          setPromotionToDelete(null);
          // Refresh the promotions lists
          refreshPromotions();
        })
        .catch(function (error) {
          console.error('Error deleting promotion:', error);
          setLoading(false);
          setDeleteDialogOpen(false);
          setPromotionToDelete(null);
        });
    }
  };

  const refreshPromotions = () => {
    get(`/admin/promotions?status=active`).then(function (response) {
      setActivePromotions(response.promotions);
    });
    get(`/admin/promotions?status=inactive`).then(function (response) {
      setInactivePromotions(response.promotions);
    });
  };

  const handleDuplicateClick = (promotion) => {
    setLoading(true);
    post(`/admin/promotions/${promotion.promotionId}/duplicate`)
      .then(function (response) {
        setLoading(false);
        // Refresh the promotions lists after duplication
        refreshPromotions();
      })
      .catch(function (error) {
        console.error('Error duplicating promotion:', error);
        setLoading(false);
      });
  };

  const isLoading = () => {
    return activePromotions === null || inactivePromotions === null || loading;
  };

  return (
    <>
      <Helmet>
        <title>Promotions </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Promotions
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreatePromotion}
          >
            + Promotion
          </Button>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Active Promotions" />
            <Tab label="Inactive Promotions" />
          </Tabs>
        </Box>

        <Card>
          {isLoading() ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <PromotionsTable
                promotions={getCurrentPromotions()}
                onDelete={handleDeleteClick}
                onDuplicate={handleDuplicateClick}
              />
            </Scrollbar>
          )}
        </Card>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Promotion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the promotion "{promotionToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
